/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Grid, TextField, Chip, IconButton, Tooltip, Button, Box,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableRow, Typography, Autocomplete,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MedicationIcon from '@mui/icons-material/Medication';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../../../api';
import KeyboardHintBar from 'components/KeyboardHintBar';
import CreatableSelect from 'components/CreatableSelect';
import MiniMasterDialog from 'components/MiniMasterDialog';

const TEAL = '#00798E';
const H = 45;

const DRUG_SCHED_OPTIONS = ['NONE', 'OTC', 'H', 'H1', 'X', 'G', 'J', 'C', 'L'].map(s => ({
  id: s,
  label: s === 'NONE' ? 'NONE (no schedule)' : `Schedule ${s}`,
}));

const SELLABLE_OPTIONS = [
  { id: 'Y', label: 'Yes — sell loose' },
  { id: 'N', label: 'No — full unit only' },
];

// Inline "New HSN Code" quick-create — same shape as ItemQuickAdd's HSN
// mini-config (GST% auto-splits into CGST/SGST), for the "Select / create"
// HSN field below.
const HSN_MINI_CONFIG = {
  title: 'New HSN Code',
  fields: [
    { key: 'hsn_code', label: 'HSN Code *', required: true },
    { key: 'description', label: 'Description *', required: true, upper: true },
    { key: 'gst_percentage', label: 'GST % *', required: true, type: 'number', helperText: 'CGST/SGST auto-split' },
    { key: 'cess_percentage', label: 'Cess %', type: 'number', default: '0' },
  ],
  build: (f) => {
    const g = parseFloat(f.gst_percentage) || 0;
    return {
      hsn_code: f.hsn_code.trim(), description: f.description.trim(),
      gst_percentage: g, cgst_percentage: g / 2, sgst_percentage: g / 2, igst_percentage: g,
      cess_percentage: parseFloat(f.cess_percentage) || 0,
    };
  },
  create: (p) => api.pharmacy.createHsn(p),
};

const acSx = {
  '& .MuiInputBase-root': { height: H, alignItems: 'center', py: '0 !important' },
  '& .MuiAutocomplete-input': { py: '0 !important' },
};

const acSxUpper = {
  ...acSx,
  '& .MuiInputBase-input': { textTransform: 'uppercase' },
};

const multiSxUpper = {
  '& .MuiInputBase-input': { textTransform: 'uppercase' },
};

const btnTeal = {
  background: TEAL,
  color: 'white !important',
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 1.5,
  px: 3,
  boxShadow: 'none',
  '&:hover': { background: '#016476', boxShadow: 'none', color: 'white !important' },
  '&.Mui-disabled': { background: '#b0b0b0', color: 'white !important' },
};

const TH = {
  py: 1.2, px: 2,
  textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 700,
  color: 'text.secondary', opacity: 0.7,
  borderBottom: '1.5px solid #f0f2f5',
  whiteSpace: 'nowrap', letterSpacing: '0.07em',
  bgcolor: 'transparent',
};
const td = (last) => ({
  py: 1.25, px: 2,
  borderBottom: last ? 'none' : '1px solid rgba(0,0,0,0.06)',
  fontSize: '0.8125rem',
});

function SectionHeader({ title }) {
  return (
    <MDTypography
      variant="subtitle1"
      fontWeight="bold"
      sx={{
        py: 1,
        mb: 2.5,
        mt: 3,
        borderBottom: `1.5px solid ${TEAL}`,
        color: TEAL,
        letterSpacing: '0.02em',
      }}
    >
      {title}
    </MDTypography>
  );
}

function calcSubPrice(mrp, count) {
  const m = parseFloat(mrp);
  const c = parseInt(count, 10);
  return (!isNaN(m) && !isNaN(c) && c > 0) ? (m / c) : null;
}

function PriceInfoCard({ mrpLabel, mrp, purchasePrice, subUnitCount, subUnitName, discount, hsnData }) {
  const mrpVal = parseFloat(mrp);
  if (!mrp || isNaN(mrpVal) || mrpVal <= 0) return null;

  const gst  = parseFloat(hsnData?.gst_percentage)  || 0;
  const cgst = parseFloat(hsnData?.cgst_percentage) || 0;
  const sgst = parseFloat(hsnData?.sgst_percentage) || 0;
  const cess = parseFloat(hsnData?.cess_percentage) || 0;
  const disc = parseFloat(discount) || 0;
  const count = parseInt(subUnitCount, 10);
  const hasSub = !isNaN(count) && count > 0 && !!subUnitName;
  // When base and sub share the same unit name (e.g., "Injection"), distinguish by quantity
  const sameUnit = hasSub && subUnitName.toLowerCase() === mrpLabel.toLowerCase();
  const baseColLabel = sameUnit ? `${count} ${mrpLabel}s` : mrpLabel;
  const subColLabel  = sameUnit ? `1 ${subUnitName}`    : subUnitName;

  const purchase = parseFloat(purchasePrice);
  const hasPurchase = !isNaN(purchase) && purchase > 0;

  // MRP is GST-inclusive: Base = MRP / (1 + GST/100)
  const basePrice    = mrpVal / (1 + gst / 100);
  const gstAmtOnMrp  = mrpVal - basePrice;
  const discAmt      = basePrice * disc / 100;
  const taxableVal   = basePrice - discAmt;
  const cgstOnSell   = taxableVal * cgst / 100;
  const sgstOnSell   = taxableVal * sgst / 100;
  const cessOnSell   = taxableVal * cess / 100;
  const gstOnSell    = taxableVal * gst / 100;
  const sellingPrice = taxableVal + gstOnSell;
  const purchaseBase = hasPurchase ? purchase / (1 + gst / 100) : null;
  const margin       = hasPurchase ? sellingPrice - purchase : null;
  const marginPct    = (margin !== null && purchase > 0) ? (margin / purchase * 100) : null;

  const f  = v => `₹ ${v.toFixed(2)}`;
  const fs = v => hasSub ? `₹ ${(v / count).toFixed(2)}` : null;

  const BDRow = ({ label, base, sub, color, divTop, bold, indent }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', py: 0.6, borderTop: divTop ? `1.5px solid ${TEAL}40` : '1px solid rgba(0,0,0,0.05)' }}>
      <Typography style={{ flex: 1, fontSize: indent ? '0.7rem' : '0.74rem', color: color || (indent ? '#999' : '#555'), fontWeight: bold ? 700 : 400, paddingLeft: indent ? 12 : 0 }}>{label}</Typography>
      <Typography style={{ minWidth: 100, textAlign: 'right', fontSize: '0.74rem', fontWeight: bold ? 800 : 600, color: color || '#333', fontFamily: 'monospace' }}>{base}</Typography>
      {hasSub && <Typography style={{ minWidth: 90, textAlign: 'right', fontSize: '0.74rem', fontWeight: bold ? 700 : 500, color: color ? color + 'bb' : '#666', fontFamily: 'monospace' }}>{sub || '—'}</Typography>}
    </Box>
  );

  return (
    <Box sx={{ border: `1px solid ${TEAL}40`, borderRadius: 2, overflow: 'hidden', mt: 1 }}>
      <Box sx={{ background: `linear-gradient(90deg, ${TEAL} 0%, #005469 100%)`, px: 2, py: 0.9, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography style={{ color: '#fff', fontWeight: 700, fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            {hsnData
              ? `Price Breakdown · GST ${gst}% (CGST ${cgst}% + SGST ${sgst}%${cess > 0 ? ` + Cess ${cess}%` : ''})`
              : 'Price Breakdown · No HSN selected (GST = 0%)'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexShrink: 0 }}>
          <Typography style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.6rem', textTransform: 'uppercase' }}>{baseColLabel}</Typography>
          {hasSub && <Typography style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem', textTransform: 'uppercase', borderLeft: '1px solid rgba(255,255,255,0.25)', paddingLeft: 8 }}>{subColLabel}</Typography>}
        </Box>
      </Box>

      <Box sx={{ px: 2, py: 1 }}>
        {hasSub && sameUnit && (
          <Box sx={{ mb: 0.5, pb: 0.5, borderBottom: '1px dashed rgba(0,121,142,0.15)', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Typography style={{ fontSize: '0.62rem', color: '#aaa', minWidth: 100, textAlign: 'right' }}>{baseColLabel}</Typography>
            <Typography style={{ fontSize: '0.62rem', color: '#aaa', minWidth: 90, textAlign: 'right' }}>{subColLabel}</Typography>
          </Box>
        )}
        <BDRow label="MRP (incl. GST) — printed on pack" base={f(mrpVal)} sub={fs(mrpVal)} bold />
        {gst > 0 && <BDRow label={`Less: GST included in MRP (${gst}%)`} base={`− ${f(gstAmtOnMrp)}`} sub={hasSub ? `− ${fs(gstAmtOnMrp)}` : null} color="#c62828" />}
        {gst > 0 && cgst > 0 && <BDRow label={`↳ CGST ${cgst}% + SGST ${sgst}%${cess > 0 ? ` + Cess ${cess}%` : ''}`} base="" sub={null} color="#e57373" indent />}
        <BDRow label="Base Price (ex-GST)" base={f(basePrice)} sub={fs(basePrice)} color={TEAL} bold />

        {disc > 0 && (
          <>
            <BDRow label={`Less: Trade Discount (${disc}%)`} base={`− ${f(discAmt)}`} sub={hasSub ? `− ${fs(discAmt)}` : null} color="#c62828" />
            <BDRow label="Taxable Value (after discount)" base={f(taxableVal)} sub={fs(taxableVal)} color="#1565c0" bold />
          </>
        )}

        {gst > 0 && (
          <>
            <BDRow label={`Add: GST on Selling (${gst}%)`} base={`+ ${f(gstOnSell)}`} sub={hasSub ? `+ ${fs(gstOnSell)}` : null} color="#2e7d32" />
            {cgst > 0 && <BDRow label={`↳ CGST: ${f(cgstOnSell)}  SGST: ${f(sgstOnSell)}${cessOnSell > 0 ? `  Cess: ${f(cessOnSell)}` : ''}`} base="" sub={null} color="#558b2f" indent />}
          </>
        )}

        <BDRow divTop label="Selling Price (incl. GST)" base={f(sellingPrice)} sub={fs(sellingPrice)} color={TEAL} bold />

        {hasPurchase && (
          <>
            <BDRow label="Purchase Price (incl. GST)" base={f(purchase)} sub={fs(purchase)} color="#555" />
            {purchaseBase !== null && <BDRow label="Purchase Base (ex-GST)" base={f(purchaseBase)} sub={fs(purchaseBase)} color="#888" indent />}
            {margin !== null && (
              <BDRow
                label={`Margin${marginPct !== null ? ` (${marginPct.toFixed(1)}%)` : ''}`}
                base={`${margin >= 0 ? '+ ' : '− '}${f(Math.abs(margin))}`}
                sub={hasSub ? `${margin >= 0 ? '+ ' : '− '}${fs(Math.abs(margin))}` : null}
                color={margin >= 0 ? '#2e7d32' : '#c62828'}
                bold
              />
            )}
          </>
        )}

        {hasSub && (
          <Box sx={{ mt: 0.5, pt: 0.75, borderTop: '1px dashed rgba(0,121,142,0.2)', display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
            <Typography style={{ fontSize: '0.68rem', color: '#999' }}>
              {sameUnit ? `${count} ${mrpLabel}s per pack` : `1 ${mrpLabel} = ${count} ${subUnitName}s`}
            </Typography>
            <Typography style={{ fontSize: '0.68rem', color: TEAL, fontWeight: 600 }}>{`MRP / ${subColLabel}: ${f(mrpVal / count)}`}</Typography>
            <Typography style={{ fontSize: '0.68rem', color: TEAL, fontWeight: 700 }}>{`Selling / ${subColLabel}: ${f(sellingPrice / count)}`}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
function VariantDialog({ open, onClose, onSaved, itemId, initial, item, units, hsnData }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    variant_name: '', variant_value: '', variant_unit: '', mrp_per_base_unit: '',
    sub_units_per_base: '', is_default: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(initial ? {
      variant_name: initial.variant_name,
      variant_value: initial.variant_value || '',
      variant_unit: initial.variant_unit || '',
      mrp_per_base_unit: initial.mrp_per_base_unit ?? '',
      sub_units_per_base: initial.sub_units_per_base ?? '',
      is_default: initial.is_default === 'Y',
    } : { variant_name: '', variant_value: '', variant_unit: '', mrp_per_base_unit: '', sub_units_per_base: '', is_default: false });
  }, [initial, open]);

  const handleSave = async () => {
    if (!form.variant_name.trim()) { Swal.fire('Validation', 'Variant name is required', 'warning'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        is_default: form.is_default ? 'Y' : 'N',
        mrp_per_base_unit: form.mrp_per_base_unit !== '' ? parseFloat(form.mrp_per_base_unit) : undefined,
        sub_units_per_base: form.sub_units_per_base !== '' ? parseInt(form.sub_units_per_base, 10) : undefined,
      };
      if (isEdit) await api.pharmacy.updateVariant(itemId, initial.id, payload);
      else await api.pharmacy.addVariant(itemId, payload);
      Swal.fire({ icon: 'success', title: 'Saved', toast: true, position: 'top-end', showConfirmButton: false, timer: 1800, timerProgressBar: true });
      onSaved(); onClose();
    } catch { /* handled */ } finally { setSaving(false); }
  };

  const effectiveMrp = form.mrp_per_base_unit || item?.mrp_per_base_unit;
  const effectiveCount = form.sub_units_per_base || item?.sub_units_per_base;
  const baseUnitName = units.find(u => u.id === item?.base_unit_id)?.name || 'Base Unit';
  const subUnitName = units.find(u => u.id === item?.sub_unit_id)?.name || '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ color: TEAL, fontWeight: 700, borderBottom: '1px solid #f0f2f5', pb: 1.5 }}>
        {isEdit ? 'Edit Variant' : 'Add Variant'}
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Variant Name *" value={form.variant_name}
              onChange={e => setForm(p => ({ ...p, variant_name: e.target.value.toUpperCase() }))}
              helperText='e.g. "STRIP-15", "VIAL-5ML"' sx={acSxUpper} InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth label="Variant Value" value={form.variant_value}
              onChange={e => setForm(p => ({ ...p, variant_value: e.target.value.toUpperCase() }))}
              helperText='Numeric part e.g. "15"' sx={acSxUpper}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth label="Variant Unit" value={form.variant_unit}
              onChange={e => setForm(p => ({ ...p, variant_unit: e.target.value.toUpperCase() }))}
              helperText='Unit label e.g. "TABLETS", "ML"' sx={acSxUpper}
            />
          </Grid>

          <Grid item xs={12}><Box sx={{ borderTop: '1px solid #f0f2f5', mt: 0.5 }} /></Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth label={`MRP / ${baseUnitName} (₹)`} type="number"
              value={form.mrp_per_base_unit}
              onChange={e => setForm(p => ({ ...p, mrp_per_base_unit: e.target.value }))}
              helperText={item?.mrp_per_base_unit ? `Item default: ₹${parseFloat(item.mrp_per_base_unit).toFixed(2)}` : 'Leave blank to use item MRP'}
              inputProps={{ min: 0, step: 0.01 }} sx={acSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth label={`Sub-units / ${baseUnitName}`} type="number"
              value={form.sub_units_per_base}
              onChange={e => setForm(p => ({ ...p, sub_units_per_base: e.target.value }))}
              helperText={item?.sub_units_per_base ? `Item default: ${item.sub_units_per_base}` : `e.g. 15 ${subUnitName.toLowerCase() || 'tablets'} per ${baseUnitName.toLowerCase()}`}
              inputProps={{ min: 1 }} sx={acSx}
            />
          </Grid>

          {effectiveMrp && (
            <Grid item xs={12}>
              <PriceInfoCard
                mrpLabel={baseUnitName}
                mrp={effectiveMrp}
                purchasePrice={null}
                subUnitCount={effectiveCount}
                subUnitName={subUnitName}
                discount={item?.discount_percentage || 0}
                hsnData={hsnData}
              />
            </Grid>
          )}
          {item?.discount_percentage > 0 && (
            <Grid item xs={12}>
              <Typography style={{ fontSize: '0.72rem', color: '#888', fontStyle: 'italic' }}>
                * Discount ({item.discount_percentage}%) inherited from primary item
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={form.is_default} onChange={e => setForm(p => ({ ...p, is_default: e.target.checked }))} sx={{ '& .Mui-checked + .MuiSwitch-track': { bgcolor: TEAL } }} />}
              label="Set as default variant"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1.5, borderTop: '1px solid #f0f2f5', gap: 1 }}>
        <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 600, color: '#016476 !important', borderColor: '#016476', '&:hover': { borderColor: TEAL, bgcolor: 'rgba(0,121,142,0.06)' } }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={btnTeal}>{saving ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function PriceDialog({ open, onClose, onSaved, itemId, item, variants, units, hsnData, initial }) {
  const isEdit = !!initial;
  const activeVariants = (variants || []).filter(v => v.is_deleted !== 'Y');
  const variantOptions = [
    { id: null, label: '(Base Item — no variant)' },
    ...activeVariants.map(v => ({ id: v.id, label: v.variant_name, _v: v })),
  ];

  const blankForm = { variant_id: null, mrp: '', purchase_price: '', discount_percentage: '0', effective_from: new Date().toISOString().split('T')[0], remarks: '' };
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          variant_id: initial.irm_pharm_item_variants_id ?? null,
          mrp: initial.mrp !== null && initial.mrp !== undefined ? String(initial.mrp) : '',
          purchase_price: initial.purchase_price !== null && initial.purchase_price !== undefined ? String(initial.purchase_price) : '',
          discount_percentage: initial.discount_percentage !== null && initial.discount_percentage !== undefined ? String(initial.discount_percentage) : '0',
          effective_from: initial.effective_from || new Date().toISOString().split('T')[0],
          remarks: initial.remarks || '',
        });
      } else {
        setForm(blankForm);
      }
    }
  }, [open, initial]);

  const selectedVariant = activeVariants.find(v => v.id === form.variant_id) || null;
  const subUnitCount = selectedVariant?.sub_units_per_base || item?.sub_units_per_base;
  const baseUnitName = units.find(u => u.id === item?.base_unit_id)?.name || 'Base Unit';
  const subUnitName = units.find(u => u.id === item?.sub_unit_id)?.name || '';

  const handleSave = async () => {
    if (!form.mrp || !form.effective_from) { Swal.fire('Validation', 'MRP and effective date are required', 'warning'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.pharmacy.updatePrice(itemId, initial.id, {
          mrp: parseFloat(form.mrp),
          purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : undefined,
          discount_percentage: parseFloat(form.discount_percentage) || 0,
          effective_from: form.effective_from,
          remarks: form.remarks || undefined,
        });
        Swal.fire({ icon: 'success', title: 'Price updated', toast: true, position: 'top-end', showConfirmButton: false, timer: 1800, timerProgressBar: true });
      } else {
        await api.pharmacy.setPrice(itemId, {
          variant_id: form.variant_id || undefined,
          mrp: parseFloat(form.mrp),
          purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : undefined,
          discount_percentage: parseFloat(form.discount_percentage) || 0,
          effective_from: form.effective_from,
          remarks: form.remarks || undefined,
        });
        Swal.fire({ icon: 'success', title: 'Price set', toast: true, position: 'top-end', showConfirmButton: false, timer: 1800, timerProgressBar: true });
      }
      onSaved(); onClose();
    } catch { /* handled */ } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ color: TEAL, fontWeight: 700, borderBottom: '1px solid #f0f2f5', pb: 1.5 }}>
        {isEdit ? 'Edit Price Record' : 'Set New Price'}
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Grid container spacing={2}>
          {activeVariants.length > 0 && (
            <Grid item xs={12}>
              <Autocomplete
                options={variantOptions}
                getOptionLabel={o => o.label || ''}
                value={variantOptions.find(o => o.id === form.variant_id) || variantOptions[0]}
                onChange={(_, v) => setForm(p => ({ ...p, variant_id: v?.id ?? null }))}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                disableClearable
                renderInput={params => <TextField {...params} label="For Variant / Base Item" sx={acSx} />}
              />
            </Grid>
          )}

          <Grid item xs={6}>
            <TextField
              fullWidth label={`MRP / ${baseUnitName} (₹) *`} type="number"
              value={form.mrp} onChange={e => setForm(p => ({ ...p, mrp: e.target.value }))}
              inputProps={{ min: 0, step: 0.01 }} sx={acSx} InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth label={`Purchase / ${baseUnitName} (₹)`} type="number"
              value={form.purchase_price} onChange={e => setForm(p => ({ ...p, purchase_price: e.target.value }))}
              inputProps={{ min: 0, step: 0.01 }} sx={acSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth label="Discount %" type="number"
              value={form.discount_percentage} onChange={e => setForm(p => ({ ...p, discount_percentage: e.target.value }))}
              inputProps={{ min: 0, max: 100, step: 0.01 }} sx={acSx} InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth label="Effective From *" type="date"
              value={form.effective_from} onChange={e => setForm(p => ({ ...p, effective_from: e.target.value }))}
              InputLabelProps={{ shrink: true }} sx={acSx}
            />
          </Grid>

          {form.mrp && (
            <Grid item xs={12}>
              <PriceInfoCard
                mrpLabel={selectedVariant ? `${baseUnitName} (${selectedVariant.variant_name})` : baseUnitName}
                mrp={form.mrp}
                purchasePrice={form.purchase_price}
                subUnitCount={subUnitCount}
                subUnitName={subUnitName}
                discount={form.discount_percentage}
                hsnData={hsnData}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth label="Remarks"
              value={form.remarks} onChange={e => setForm(p => ({ ...p, remarks: e.target.value.toUpperCase() }))}
              sx={acSxUpper}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1.5, borderTop: '1px solid #f0f2f5', gap: 1 }}>
        <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 600, color: '#016476 !important', borderColor: '#016476', '&:hover': { borderColor: TEAL, bgcolor: 'rgba(0,121,142,0.06)' } }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={btnTeal}>{saving ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ItemDetail() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const isNew = itemId === 'new';
  const [item, setItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [units, setUnits] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);

  const [variantDialog, setVariantDialog] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [priceDialog, setPriceDialog] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  // Inline "New HSN Code" quick-create, opened from the HSN field below when
  // the code the user needs isn't in the list yet. `hsnMiniName` seeds the
  // dialog's first field with whatever the user had typed.
  const [hsnMiniOpen, setHsnMiniOpen] = useState(false);
  const [hsnMiniName, setHsnMiniName] = useState('');

  const [form, setForm] = useState({
    irm_pharm_categories_id: '',
    name: '',
    generic_name: '',
    description: '',
    drug_schedule: 'NONE',
    irm_pharm_manufacturers_id: '',
    irm_pharm_hsn_codes_id: '',
    base_unit_id: '',
    sub_unit_id: '',
    sub_units_per_base: '',
    is_sub_unit_sellable: 'N',
    mrp_per_base_unit: '',
    discount_percentage: '0',
  });

  const loadMasters = async () => {
    try {
      const [cats, mfrs, us, hsns, sups] = await Promise.all([
        api.pharmacy.listCategories({ is_active: 'Y', limit: 200 }),
        api.pharmacy.listManufacturers({ is_active: 'Y', limit: 200 }),
        api.pharmacy.listUnits({ is_active: 'Y', limit: 200 }),
        api.pharmacy.listHsn({ is_active: 'Y', limit: 500 }),
        api.pharmacy.listSuppliers({ is_active: 'Y', limit: 200 }),
      ]);
      setCategories(cats.data || []);
      setManufacturers(mfrs.data || []);
      setUnits(us.data || []);
      setHsnCodes(hsns.data || []);
      setSuppliers(sups.data || []);
    } catch { /* handled */ }
  };

  const loadItem = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await api.pharmacy.getItemById(itemId);
      const d = res.data;
      setItem(d);
      setForm({
        irm_pharm_categories_id: d.irm_pharm_categories_id || '',
        name: d.name || '',
        generic_name: d.generic_name || '',
        description: d.description || '',
        drug_schedule: d.drug_schedule || 'NONE',
        irm_pharm_manufacturers_id: d.irm_pharm_manufacturers_id || '',
        irm_pharm_hsn_codes_id: d.irm_pharm_hsn_codes_id || '',
        base_unit_id: d.base_unit_id || '',
        sub_unit_id: d.sub_unit_id || '',
        sub_units_per_base: d.sub_units_per_base ?? '',
        is_sub_unit_sellable: d.is_sub_unit_sellable || 'N',
        mrp_per_base_unit: d.mrp_per_base_unit ?? '',
        discount_percentage: d.discount_percentage ?? '0',
      });
    } catch { /* handled */ }
  }, [itemId, isNew]);

  const loadPriceHistory = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await api.pharmacy.getPriceHistory(itemId);
      setPriceHistory(res.data || []);
    } catch { /* handled */ }
  }, [itemId, isNew]);

  useEffect(() => { loadMasters(); loadItem(); }, [loadItem]);
  useEffect(() => { if (!isNew) loadPriceHistory(); }, [loadPriceHistory, isNew]);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleCategoryChange = (catObj) => {
    if (!catObj) { setField('irm_pharm_categories_id', ''); return; }
    const smartDefaults = catObj.name?.toLowerCase().includes('medicine');
    const strip = smartDefaults && units.find(u => u.short_name?.toUpperCase() === 'STR' || u.name?.toLowerCase().includes('strip'));
    const piece = smartDefaults && units.find(u => u.short_name?.toUpperCase() === 'PCS' || u.name?.toLowerCase().includes('piece'));
    setForm(p => ({
      ...p,
      irm_pharm_categories_id: catObj.id,
      ...(strip ? { base_unit_id: strip.id } : {}),
      ...(piece ? { sub_unit_id: piece.id } : {}),
      ...(smartDefaults ? { is_sub_unit_sellable: 'Y' } : {}),
    }));
  };

  const acVal = (list, id) => (id !== '' && id !== null && id !== undefined ? list.find(o => String(o.id) === String(id)) || null : null);

  const handleHsnCreated = (rec) => {
    setHsnCodes(p => [rec, ...p]);
    setField('irm_pharm_hsn_codes_id', rec.id);
  };



  const handleSave = async () => {
    if (!form.name?.trim() || !form.irm_pharm_categories_id || !form.base_unit_id) {
      Swal.fire('Validation', 'Name, Category and Base Unit are required', 'warning'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        irm_pharm_categories_id: parseInt(form.irm_pharm_categories_id),
        irm_pharm_manufacturers_id: form.irm_pharm_manufacturers_id ? parseInt(form.irm_pharm_manufacturers_id) : undefined,
        irm_pharm_hsn_codes_id: form.irm_pharm_hsn_codes_id ? parseInt(form.irm_pharm_hsn_codes_id) : undefined,
        base_unit_id: parseInt(form.base_unit_id),
        sub_unit_id: form.sub_unit_id ? parseInt(form.sub_unit_id) : undefined,
        sub_units_per_base: form.sub_units_per_base ? parseInt(form.sub_units_per_base) : undefined,
        mrp_per_base_unit: form.mrp_per_base_unit !== '' ? parseFloat(form.mrp_per_base_unit) : undefined,
        discount_percentage: parseFloat(form.discount_percentage) || 0,
      };
      if (isNew) {
        const res = await api.pharmacy.createItem(payload);
        const newId = res.data?.id;
        if (newId) {
          await api.pharmacy.addVariant(newId, {
            variant_name: 'STANDARD',
            mrp_per_base_unit: payload.mrp_per_base_unit || undefined,
            sub_units_per_base: payload.sub_units_per_base || undefined,
            is_default: 'Y',
          });
        }
        Swal.fire({ icon: 'success', title: 'Item Created', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000, timerProgressBar: true });
        navigate(`/pharmacy/mm/items/${newId}`, { replace: true });
      } else {
        await api.pharmacy.updateItem(itemId, payload);
        Swal.fire({ icon: 'success', title: 'Saved', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000, timerProgressBar: true });
        loadItem();
      }
    } catch { /* handled */ } finally { setSaving(false); }
  };

  // Ctrl+S saves the item (Tally-style), unless a sub-dialog is open.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        if (variantDialog || priceDialog) return;
        e.preventDefault();
        if (!saving) handleSave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemoveVariant = async (v) => {
    const r = await Swal.fire({ title: 'Remove variant?', text: v.variant_name, icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Remove' });
    if (!r.isConfirmed) return;
    try { await api.pharmacy.removeVariant(itemId, v.id); loadItem(); } catch { /* handled */ }
  };

  const handleDeletePrice = async (p) => {
    const r = await Swal.fire({
      title: 'Delete price record?',
      html: `MRP ₹${parseFloat(p.mrp).toFixed(2)} · Effective ${p.effective_from}${p.variant ? ` · ${p.variant.variant_name}` : ''}`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#d33', confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (!r.isConfirmed) return;
    try { await api.pharmacy.deletePrice(itemId, p.id); loadPriceHistory(); } catch { /* handled */ }
  };

  const handleUnlinkSupplier = async (mapping) => {
    const r = await Swal.fire({ title: 'Unlink supplier?', text: mapping.supplier?.name, icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33' });
    if (!r.isConfirmed) return;
    try { await api.pharmacy.unlinkSupplier(itemId, mapping.id); loadItem(); } catch { /* handled */ }
  };

  const handleLinkSupplier = async (supplierId) => {
    try {
      const active = (item?.item_suppliers || []).filter(s => s.is_deleted !== 'Y');
      await api.pharmacy.linkSupplier(itemId, {
        irm_pharm_suppliers_id: parseInt(supplierId),
        is_primary: active.length === 0 ? 'Y' : 'N',
      });
      loadItem();
    } catch { /* handled */ }
  };

  const handleTogglePrimary = async (mapping) => {
    const activeSuppliers = (item?.item_suppliers || []).filter(s => s.is_deleted !== 'Y');
    if (mapping.is_primary === 'Y') {
      const otherPrimaries = activeSuppliers.filter(s => s.id !== mapping.id && s.is_primary === 'Y');
      if (otherPrimaries.length === 0) {
        Swal.fire({ icon: 'warning', title: 'Cannot Remove Primary', text: 'At least one supplier must be marked as primary. Set another supplier as primary first.', confirmButtonColor: TEAL });
        return;
      }
      await api.pharmacy.updateSupplierLink(itemId, mapping.id, { is_primary: 'N' });
    } else {
      await api.pharmacy.updateSupplierLink(itemId, mapping.id, { is_primary: 'Y' });
    }
    loadItem();
  };

  const linkedSupplierIds = (item?.item_suppliers || []).filter(s => s.is_deleted !== 'Y').map(s => s.irm_pharm_suppliers_id);
  const availableSuppliers = suppliers.filter(s => !linkedSupplierIds.includes(s.id));
  const selectedHsn = hsnCodes.find(h => String(h.id) === String(form.irm_pharm_hsn_codes_id));

  const gradientHeader = (title, action) => (
    <MDBox
      mx={2} mt={-3} py={2.5} px={2}
      variant="gradient" bgColor="logo" borderRadius="lg" coloredShadow="logo"
      display="flex" justifyContent="space-between" alignItems="center"
    >
      <MDTypography variant="h6" color="white">{title}</MDTypography>
      {action}
    </MDBox>
  );

  const outlineWhiteBtn = (label, icon, onClick) => (
    <Button
      size="small" variant="outlined" startIcon={icon} onClick={onClick}
      sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
    >
      {label}
    </Button>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>

        <KeyboardHintBar storageKey="pharm-item-detail">
          <b>Ctrl+S</b> Save · <b>Tab</b> next field
        </KeyboardHintBar>

        {/* ── Page header card ── */}
        <Card sx={{ mb: 4, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,121,142,0.22)' }}>
          {/* Gradient banner */}
          <Box sx={{
            background: `linear-gradient(135deg, ${TEAL} 0%, #005469 100%)`,
            px: 3, pt: 2.5, pb: 3, position: 'relative', overflow: 'hidden',
          }}>
            {/* Decorative background circles */}
            <Box sx={{ position: 'absolute', top: -30, right: 40, width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', bottom: -40, right: -20, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', top: 10, right: 160, width: 60, height: 60, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

            {/* Breadcrumb */}
            <Box display="flex" alignItems="center" gap={0.5} mb={2}>
              <Button
                size="small" startIcon={<ArrowBackIcon sx={{ fontSize: '13px !important' }} />}
                onClick={() => navigate('/pharmacy/mm', { state: { tab: 0 } })}
                sx={{ color: 'rgba(255,255,255,0.72)', textTransform: 'none', fontWeight: 500, fontSize: '0.75rem', p: 0, minWidth: 0, lineHeight: 1, '&:hover': { color: '#fff', bgcolor: 'transparent' } }}
              >
                Material Management
              </Button>
              <NavigateNextIcon sx={{ color: 'rgba(255,255,255,0.38)', fontSize: 14 }} />
              <Typography style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.75rem', fontWeight: 500 }}>Items</Typography>
              <NavigateNextIcon sx={{ color: 'rgba(255,255,255,0.38)', fontSize: 14 }} />
              <Typography style={{ color: '#ffffff', fontSize: '0.75rem', fontWeight: 700 }}>
                {isNew ? 'New Item' : (item?.name || 'Edit')}
              </Typography>
            </Box>

            {/* Title + actions row */}
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              {/* Left: icon + title */}
              <Box display="flex" alignItems="center" gap={2}>
                <Box sx={{
                  width: 58, height: 58, flexShrink: 0, borderRadius: '16px',
                  background: 'rgba(255,255,255,0.18)',
                  border: '1.5px solid rgba(255,255,255,0.28)',
                  backdropFilter: 'blur(6px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <MedicationIcon style={{ color: '#ffffff', fontSize: 30 }} />
                </Box>
                <Box>
                  <Typography style={{ color: '#ffffff', fontWeight: 800, fontSize: '1.5rem', lineHeight: 1.15, letterSpacing: '-0.01em' }}>
                    {isNew ? 'New Item' : (item?.name || 'Item Detail')}
                  </Typography>
                  <Typography style={{ color: 'rgba(255,255,255,0.62)', fontSize: '0.82rem', marginTop: 4 }}>
                    {isNew
                      ? 'Fill in the details to register a new pharmacy item'
                      : `${item?.category?.name || 'Pharmacy'}${item?.generic_name ? ' · ' + item.generic_name : ''}`}
                  </Typography>
                </Box>
                {item?.item_code && (
                  <Chip
                    label={item.item_code} size="small"
                    sx={{ fontFamily: 'monospace', bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.28)', fontWeight: 700, letterSpacing: '0.06em' }}
                  />
                )}
              </Box>

              {/* Right: status + save */}
              <Box display="flex" alignItems="center" gap={1.5}>
                {!isNew && item?.is_active !== undefined && (
                  <Chip
                    label={item.is_active === 'Y' ? '● Active' : '● Inactive'} size="small"
                    sx={{
                      bgcolor: item.is_active === 'Y' ? 'rgba(76,175,80,0.22)' : 'rgba(0,0,0,0.22)',
                      color: item.is_active === 'Y' ? '#a5d6a7' : 'rgba(255,255,255,0.55)',
                      border: `1px solid ${item.is_active === 'Y' ? 'rgba(76,175,80,0.4)' : 'rgba(255,255,255,0.18)'}`,
                      fontWeight: 700, fontSize: '0.75rem',
                    }}
                  />
                )}
                <Button
                  variant="contained" onClick={handleSave} disabled={saving}
                  sx={{ bgcolor: '#fff', color: `${TEAL} !important`, textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 3.5, py: 1.1, fontSize: '0.875rem', boxShadow: '0 4px 14px rgba(0,0,0,0.18)', '&:hover': { bgcolor: '#e8f7f9', boxShadow: '0 6px 20px rgba(0,0,0,0.22)', color: `${TEAL} !important` }, '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.5)', color: 'rgba(0,121,142,0.35) !important' } }}
                >
                  {saving ? 'Saving…' : (isNew ? 'Create Item' : 'Save Changes')}
                </Button>
              </Box>
            </Box>
          </Box>

          {/* White meta strip for existing items */}
          {!isNew && (
            <Box sx={{ px: 3, py: 1.5, bgcolor: '#fff', display: 'flex', gap: 4, flexWrap: 'wrap', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              {[
                item?.category?.name && { label: 'Category', value: item.category.name },
                item?.drug_schedule && item.drug_schedule !== 'NONE' && { label: 'Schedule', value: `Sch-${item.drug_schedule}` },
                item?.manufacturer?.name && { label: 'Manufacturer', value: item.manufacturer.name },
                item?.base_unit?.name && { label: 'Base Unit', value: item.base_unit.name },
                item?.mrp_per_base_unit !== null && item?.mrp_per_base_unit !== undefined && { label: 'MRP', value: `₹${parseFloat(item.mrp_per_base_unit).toFixed(2)}` },
              ].filter(Boolean).map(({ label, value }) => (
                <Box key={label}>
                  <Typography style={{ color: '#9e9e9e', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>{label}</Typography>
                  <Typography style={{ fontWeight: 700, fontSize: '0.82rem', color: '#333', marginTop: 2 }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Card>

        {/* ── Main info card ── */}
        <Card sx={{ mb: 4 }}>
          {/* {gradientHeader(
            isNew ? 'Item Information' : 'Basic Information',
            !isNew && item?.is_active !== undefined && (
              <Chip
                label={item.is_active === 'Y' ? 'Active' : 'Inactive'} size="small"
                sx={{ bgcolor: item.is_active === 'Y' ? '#e8f5e9' : '#f5f5f5', color: item.is_active === 'Y' ? '#2e7d32' : '#666', fontWeight: 600 }}
              />
            )
          )} */}

          <MDBox px={3} pt={3} pb={4}>
            {/* Basic Details */}
            <SectionHeader title="Basic Details" />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField
                  autoFocus={isNew}
                  fullWidth label="Item / Brand Name *"
                  value={form.name} onChange={e => setField('name', e.target.value.toUpperCase())}
                  sx={acSxUpper} InputLabelProps={{ shrink: !!form.name }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={categories}
                  getOptionLabel={o => o.name || ''}
                  value={acVal(categories, form.irm_pharm_categories_id)}
                  onChange={(_, v) => handleCategoryChange(v)}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={params => <TextField {...params} label="Category *" sx={acSx} />}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth label="Generic / Salt Name"
                  value={form.generic_name} onChange={e => setField('generic_name', e.target.value.toUpperCase())}
                  helperText='e.g. "PARACETAMOL 500MG"'
                  sx={acSxUpper} InputLabelProps={{ shrink: !!form.generic_name }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={DRUG_SCHED_OPTIONS}
                  getOptionLabel={o => o.label || ''}
                  value={DRUG_SCHED_OPTIONS.find(o => o.id === form.drug_schedule) || null}
                  onChange={(_, v) => setField('drug_schedule', v?.id || 'NONE')}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  disableClearable
                  renderInput={params => <TextField {...params} label="Drug Schedule" sx={acSx} />}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={manufacturers}
                  getOptionLabel={o => o.name || ''}
                  value={acVal(manufacturers, form.irm_pharm_manufacturers_id)}
                  onChange={(_, v) => setField('irm_pharm_manufacturers_id', v?.id || '')}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={params => <TextField {...params} label="Manufacturer" sx={acSx} />}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Description / Notes"
                  value={form.description} onChange={e => setField('description', e.target.value.toUpperCase())}
                  multiline rows={3} sx={multiSxUpper}
                />
              </Grid>
            </Grid>

            {/* HSN & GST — select first so GST rate flows into price calculation */}
            <SectionHeader title="HSN & GST" />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <CreatableSelect
                  label="HSN Code" placeholder="Select / create — sets GST"
                  options={hsnCodes.map(h => ({ value: String(h.id), label: h.hsn_code ? `${h.hsn_code} — ${h.description}` : '' }))}
                  value={form.irm_pharm_hsn_codes_id}
                  onChange={v => setField('irm_pharm_hsn_codes_id', v ? parseInt(v, 10) : '')}
                  onCreateNew={name => { setHsnMiniName(name); setHsnMiniOpen(true); }}
                />
              </Grid>
              {selectedHsn && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2, bgcolor: TEAL + '08', borderColor: TEAL + '30' }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">GST %</Typography><Typography variant="body1" fontWeight="bold">{selectedHsn.gst_percentage}%</Typography></Grid>
                      <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">CGST %</Typography><Typography variant="body1">{selectedHsn.cgst_percentage}%</Typography></Grid>
                      <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">SGST %</Typography><Typography variant="body1">{selectedHsn.sgst_percentage}%</Typography></Grid>
                      <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Cess %</Typography><Typography variant="body1">{selectedHsn.cess_percentage}%</Typography></Grid>
                      {selectedHsn.hsn_level_discount_percentage > 0 && (
                        <Grid item xs={12}><Chip label={`HSN Level Discount: ${selectedHsn.hsn_level_discount_percentage}%`} color="warning" size="small" /></Grid>
                      )}
                    </Grid>
                  </Card>
                </Grid>
              )}
            </Grid>

            {/* Pricing & Units */}
            <SectionHeader title="Pricing & Units" />
            <Grid container spacing={2}>
              {/* Row 1 — unit config */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={units}
                  getOptionLabel={o => o.name ? `${o.name} (${o.short_name})` : ''}
                  value={acVal(units, form.base_unit_id)}
                  onChange={(_, v) => setField('base_unit_id', v?.id || '')}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={params => <TextField {...params} label="Base Unit (Selling Unit) *" sx={acSx} />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={units}
                  getOptionLabel={o => o.name ? `${o.name} (${o.short_name})` : ''}
                  value={acVal(units, form.sub_unit_id)}
                  onChange={(_, v) => setField('sub_unit_id', v?.id || '')}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={params => <TextField {...params} label="Sub Unit (Breakable to)" sx={acSx} />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth label="Sub-units per Base" type="number"
                  value={form.sub_units_per_base} onChange={e => setField('sub_units_per_base', e.target.value)}
                  inputProps={{ min: 1 }}
                  helperText={(() => {
                    const bu = units.find(u => String(u.id) === String(form.base_unit_id));
                    const su = units.find(u => String(u.id) === String(form.sub_unit_id));
                    return bu && su ? `e.g. 15 ${su.name}s per ${bu.name}` : 'e.g. 15 tablets per strip';
                  })()}
                  sx={acSx} InputLabelProps={{ shrink: form.sub_units_per_base !== '' }}
                />
              </Grid>

              {/* Row 2 — pricing inputs */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={SELLABLE_OPTIONS}
                  getOptionLabel={o => o.label || ''}
                  value={SELLABLE_OPTIONS.find(o => o.id === form.is_sub_unit_sellable) || null}
                  onChange={(_, v) => setField('is_sub_unit_sellable', v?.id || 'N')}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  disableClearable
                  renderInput={params => <TextField {...params} label="Can Sell Sub-units?" sx={acSx} />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={`MRP / ${units.find(u => String(u.id) === String(form.base_unit_id))?.name || 'Base Unit'} (₹) — incl. GST`}
                  type="number"
                  value={form.mrp_per_base_unit} onChange={e => setField('mrp_per_base_unit', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={acSx} InputLabelProps={{ shrink: form.mrp_per_base_unit !== '' }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth label="Discount %" type="number"
                  value={form.discount_percentage} onChange={e => setField('discount_percentage', e.target.value)}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  helperText="Your trade discount on base price"
                  sx={acSx} InputLabelProps={{ shrink: form.discount_percentage !== '' }}
                />
              </Grid>

              {/* Price Breakdown — auto-calculated from MRP + GST + Discount */}
              {(() => {
                const mrp = parseFloat(form.mrp_per_base_unit);
                if (isNaN(mrp) || mrp <= 0) return (
                  <Grid item xs={12}>
                    <Box sx={{ py: 1.5, px: 2, border: '1px dashed rgba(0,121,142,0.25)', borderRadius: 2, textAlign: 'center' }}>
                      <Typography style={{ fontSize: '0.78rem', color: '#999' }}>
                        Enter MRP above to see the price breakdown
                        {!selectedHsn ? ' · Select HSN Code for GST-inclusive calculation' : ''}
                      </Typography>
                    </Box>
                  </Grid>
                );

                const gst  = parseFloat(selectedHsn?.gst_percentage)  || 0;
                const cgst = parseFloat(selectedHsn?.cgst_percentage) || 0;
                const sgst = parseFloat(selectedHsn?.sgst_percentage) || 0;
                const cess = parseFloat(selectedHsn?.cess_percentage) || 0;
                const disc = parseFloat(form.discount_percentage) || 0;
                const count = parseInt(form.sub_units_per_base, 10);
                const hasSub = !isNaN(count) && count > 0 && !!form.sub_unit_id;

                const subUnit  = units.find(u => String(u.id) === String(form.sub_unit_id));
                const baseUnit = units.find(u => String(u.id) === String(form.base_unit_id));
                const subName  = subUnit?.name  || 'Sub-unit';
                const baseName = baseUnit?.name || 'Base Unit';
                // When base and sub share the same unit name distinguish by quantity
                const sameUnit     = hasSub && subName.toLowerCase() === baseName.toLowerCase();
                const baseColLabel = sameUnit ? `${count} ${baseName}s` : baseName;
                const subColLabel  = sameUnit ? `1 ${subName}`          : subName;

                // Step 1: strip GST from MRP — MRP is GST-inclusive so Base = MRP / (1 + GST/100)
                const basePrice    = mrp / (1 + gst / 100);
                const gstAmtOnMrp  = mrp - basePrice;             // GST embedded in MRP

                // Step 2: apply discount on base price
                const discAmt      = basePrice * disc / 100;
                const taxableVal   = basePrice - discAmt;          // after discount

                // Step 3: add GST back on taxable value → selling price
                const cgstOnSell   = taxableVal * cgst / 100;
                const sgstOnSell   = taxableVal * sgst / 100;
                const cessOnSell   = taxableVal * cess / 100;
                const gstOnSell    = taxableVal * gst / 100;
                const sellingPrice = taxableVal + gstOnSell;

                const fmt  = v => `₹ ${v.toFixed(2)}`;
                const fmtS = v => hasSub ? `₹ ${(v / count).toFixed(2)}` : '—';

                const BDRow = ({ label, base, sub, color, divTop, bold }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', py: 0.8, borderTop: divTop ? `2px solid ${TEAL}40` : '1px solid rgba(0,0,0,0.05)' }}>
                    <Typography style={{ flex: 1, fontSize: '0.78rem', color: color || '#555', fontWeight: bold ? 700 : 400 }}>{label}</Typography>
                    <Typography style={{ minWidth: 110, textAlign: 'right', fontSize: '0.78rem', fontWeight: bold ? 800 : 600, color: color || '#333', fontFamily: 'monospace' }}>{base}</Typography>
                    {hasSub && <Typography style={{ minWidth: 100, textAlign: 'right', fontSize: '0.78rem', fontWeight: bold ? 800 : 600, color: color || '#666', fontFamily: 'monospace', opacity: 0.85 }}>{sub}</Typography>}
                  </Box>
                );

                return (
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ borderColor: TEAL + '50', borderRadius: 2, overflow: 'hidden', mt: 1 }}>
                      {/* Card header */}
                      <Box sx={{ background: `linear-gradient(90deg, ${TEAL} 0%, #005469 100%)`, px: 2.5, py: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography style={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            Price Breakdown {selectedHsn ? `· GST ${gst}% (CGST ${cgst}% + SGST ${sgst}%${cess > 0 ? ` + Cess ${cess}%` : ''})` : '· No HSN selected (GST = 0%)'}
                          </Typography>
                        </Box>
                        {hasSub && (
                          <Box sx={{ display: 'flex', gap: 3 }}>
                            <Typography style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'right' }}>Per {baseColLabel}</Typography>
                            <Typography style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'right', borderLeft: '1px solid rgba(255,255,255,0.25)', paddingLeft: 8 }}>Per {subColLabel}</Typography>
                          </Box>
                        )}
                        {!hasSub && (
                          <Typography style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Per {baseName}</Typography>
                        )}
                      </Box>

                      {/* Breakdown rows */}
                      <Box sx={{ px: 2.5, py: 1.5 }}>
                        <BDRow label={`MRP (incl. GST) — printed on pack`} base={fmt(mrp)} sub={fmtS(mrp)} color="#333" bold />
                        {gst > 0 && <BDRow label={`Less: GST included in MRP (${gst}%)`} base={`− ${fmt(gstAmtOnMrp)}`} sub={hasSub ? `− ${fmtS(gstAmtOnMrp)}` : '—'} color="#c62828" />}
                        <BDRow label={`Base Price (ex-GST)`} base={fmt(basePrice)} sub={fmtS(basePrice)} color={TEAL} bold />

                        {disc > 0 && <>
                          <BDRow label={`Less: Trade Discount (${disc}%)`} base={`− ${fmt(discAmt)}`} sub={hasSub ? `− ${fmtS(discAmt)}` : '—'} color="#c62828" />
                          <BDRow label={`Taxable Value (after ${disc}% discount)`} base={fmt(taxableVal)} sub={fmtS(taxableVal)} color="#1565c0" bold />
                        </>}

                        {gst > 0 && <>
                          <BDRow label={`Add: GST on Selling Value (${gst}%)`} base={`+ ${fmt(gstOnSell)}`} sub={hasSub ? `+ ${fmtS(gstOnSell)}` : '—'} color="#2e7d32" />
                          {cgst > 0 && <BDRow label={`    ↳ CGST (${cgst}%)`} base={`  ${fmt(cgstOnSell)}`} sub={hasSub ? `  ${fmtS(cgstOnSell)}` : '—'} color="#558b2f" />}
                          {sgst > 0 && <BDRow label={`    ↳ SGST (${sgst}%)`} base={`  ${fmt(sgstOnSell)}`} sub={hasSub ? `  ${fmtS(sgstOnSell)}` : '—'} color="#558b2f" />}
                          {cess > 0 && <BDRow label={`    ↳ Cess (${cess}%)`} base={`  ${fmt(cessOnSell)}`} sub={hasSub ? `  ${fmtS(cessOnSell)}` : '—'} color="#558b2f" />}
                        </>}

                        <BDRow divTop label={`Selling Price (incl. GST)`} base={fmt(sellingPrice)} sub={fmtS(sellingPrice)} color={TEAL} bold />

                        {hasSub && (
                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed rgba(0,121,142,0.2)', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            <Typography style={{ fontSize: '0.72rem', color: '#888' }}>
                              {sameUnit ? `${count} ${baseName}s per pack` : `1 ${baseName} = ${count} ${subName}s`}
                            </Typography>
                            <Typography style={{ fontSize: '0.72rem', color: TEAL, fontWeight: 600 }}>
                              {`MRP / ${subColLabel}: ${fmt(mrp / count)}`}
                            </Typography>
                            <Typography style={{ fontSize: '0.72rem', color: TEAL, fontWeight: 600 }}>
                              {`Selling / ${subColLabel}: ${fmt(sellingPrice / count)}`}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Card>
                  </Grid>
                );
              })()}
            </Grid>
          </MDBox>
        </Card>

        {/* ── Existing item sections ── */}
        {!isNew && (
          <>
            {/* Variants */}
            <Card sx={{ mb: 4 }}>
              {gradientHeader('Variants', outlineWhiteBtn('Add Variant', <AddIcon />, () => { setEditingVariant(null); setVariantDialog(true); }))}
              <MDBox px={2} pt={2} pb={3}>
                {(item?.variants || []).filter(v => v.is_deleted !== 'Y').length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mb: 1.5 }}>
                      No variants yet. The primary item is automatically added as the STANDARD variant on creation.
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={async () => {
                        try {
                          await api.pharmacy.addVariant(itemId, {
                            variant_name: 'STANDARD',
                            mrp_per_base_unit: item?.mrp_per_base_unit || undefined,
                            sub_units_per_base: item?.sub_units_per_base || undefined,
                            is_default: 'Y',
                          });
                          loadItem();
                          Swal.fire({ icon: 'success', title: 'Base variant created', toast: true, position: 'top-end', showConfirmButton: false, timer: 1600, timerProgressBar: true });
                        } catch { /* handled */ }
                      }}
                      sx={{ textTransform: 'none', borderColor: TEAL, color: TEAL, '&:hover': { bgcolor: TEAL + '10', borderColor: TEAL } }}
                    >
                      Create STANDARD Base Variant
                    </Button>
                  </Box>
                ) : (
                  <Table sx={{ boxShadow: 'none' }}>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={TH}>Sub-Item Code</TableCell>
                        <TableCell sx={TH}>Variant Name</TableCell>
                        <TableCell sx={TH}>Value / Unit</TableCell>
                        <TableCell sx={TH}>Sub-units / Base</TableCell>
                        <TableCell sx={TH}>MRP / Base Unit</TableCell>
                        <TableCell sx={TH}>Price / Sub-unit</TableCell>
                        <TableCell sx={TH}>Default</TableCell>
                        <TableCell sx={TH}>Actions</TableCell>
                      </TableRow>
                      {(item?.variants || []).filter(v => v.is_deleted !== 'Y').map((v, idx, arr) => {
                        const last = idx === arr.length - 1;
                        const effectiveMrp = v.mrp_per_base_unit !== null && v.mrp_per_base_unit !== undefined ? parseFloat(v.mrp_per_base_unit) : (item?.mrp_per_base_unit !== null && item?.mrp_per_base_unit !== undefined ? parseFloat(item.mrp_per_base_unit) : null);
                        const effectiveCount = v.sub_units_per_base || item?.sub_units_per_base;
                        const subUnitName = units.find(u => u.id === item?.sub_unit_id)?.name || '';
                        const subUnitPrice = calcSubPrice(effectiveMrp, effectiveCount);
                        return (
                          <TableRow key={v.id} hover sx={{ '&:hover': { bgcolor: 'rgba(0,121,142,0.03)' } }}>
                            <TableCell sx={td(last)}>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: TEAL, fontWeight: 600, fontSize: '0.72rem' }}>{v.sub_item_code || '—'}</Typography>
                            </TableCell>
                            <TableCell sx={td(last)}>
                              <Typography variant="caption" fontWeight={700}>{v.variant_name}</Typography>
                            </TableCell>
                            <TableCell sx={td(last)}>
                              <Typography variant="caption">{v.variant_value || '—'}{v.variant_unit ? ` ${v.variant_unit}` : ''}</Typography>
                            </TableCell>
                            <TableCell sx={td(last)}>
                              {effectiveCount ? (
                                <Box>
                                  <Typography variant="caption" fontWeight={600}>{effectiveCount}</Typography>
                                  {v.sub_units_per_base && v.sub_units_per_base !== item?.sub_units_per_base
                                    ? <Chip label="Override" size="small" sx={{ ml: 0.5, fontSize: '0.58rem', height: 16, bgcolor: '#fff3e0', color: '#e65100' }} />
                                    : subUnitName ? <Typography variant="caption" color="text.secondary"> {subUnitName}s</Typography> : null}
                                </Box>
                              ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                            </TableCell>
                            <TableCell sx={td(last)}>
                              <Box>
                                <Typography variant="caption" fontWeight={600} sx={{ color: v.mrp_per_base_unit !== null && v.mrp_per_base_unit !== undefined ? TEAL : 'text.secondary' }}>
                                  {effectiveMrp !== null ? `₹${effectiveMrp.toFixed(2)}` : '—'}
                                </Typography>
                                {v.mrp_per_base_unit === null || v.mrp_per_base_unit === undefined
                                  ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6rem' }}>(item default)</Typography>
                                  : <Chip label="Override" size="small" sx={{ ml: 0.5, fontSize: '0.58rem', height: 16, bgcolor: TEAL + '12', color: TEAL }} />}
                              </Box>
                            </TableCell>
                            <TableCell sx={td(last)}>
                              {subUnitPrice !== null ? (
                                <Box>
                                  <Typography variant="caption" fontWeight={700} sx={{ color: '#1565c0' }}>₹{subUnitPrice.toFixed(2)}</Typography>
                                  {subUnitName && <Typography variant="caption" color="text.secondary"> /{subUnitName}</Typography>}
                                </Box>
                              ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                            </TableCell>
                            <TableCell sx={td(last)}>
                              {v.is_default === 'Y' ? <Chip label="Default" size="small" sx={{ bgcolor: TEAL + '18', color: TEAL, fontWeight: 700, fontSize: '0.7rem' }} /> : <Typography variant="caption" color="text.secondary">—</Typography>}
                            </TableCell>
                            <TableCell sx={td(last)}>
                              <Box display="flex" gap={0.5}>
                                <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditingVariant(v); setVariantDialog(true); }}><EditIcon fontSize="small" sx={{ color: TEAL }} /></IconButton></Tooltip>
                                <Tooltip title="Remove"><IconButton size="small" onClick={() => handleRemoveVariant(v)}><DeleteIcon fontSize="small" color="error" /></IconButton></Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </MDBox>
            </Card>

            {/* Suppliers */}
            <Card sx={{ mb: 4 }}>
              {gradientHeader('Linked Suppliers')}
              <MDBox px={2} pt={2} pb={3}>
                {availableSuppliers.length > 0 && (
                  <MDBox mb={2} sx={{ maxWidth: 360 }}>
                    <Autocomplete
                      options={availableSuppliers}
                      getOptionLabel={o => o.name || ''}
                      value={null}
                      onChange={(_, v) => { if (v) handleLinkSupplier(v.id); }}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      renderInput={params => <TextField {...params} label="Link a Supplier" sx={acSx} />}
                    />
                  </MDBox>
                )}
                {(item?.item_suppliers || []).filter(s => s.is_deleted !== 'Y').length === 0 ? (
                  <Typography sx={{ textAlign: 'center', py: 3, color: 'text.secondary', fontSize: '0.875rem' }}>No suppliers linked yet.</Typography>
                ) : (
                  <Table sx={{ boxShadow: 'none' }}>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={TH}>Supplier Name</TableCell>
                        <TableCell sx={TH}>GST Number</TableCell>
                        <TableCell sx={TH}>Primary</TableCell>
                        <TableCell sx={TH}>Actions</TableCell>
                      </TableRow>
                      {(item?.item_suppliers || []).filter(s => s.is_deleted !== 'Y').map((s, idx, arr) => {
                        const last = idx === arr.length - 1;
                        return (
                          <TableRow key={s.id} hover sx={{ '&:hover': { bgcolor: 'rgba(0,121,142,0.03)' } }}>
                            <TableCell sx={td(last)}><Typography variant="caption" fontWeight={600}>{s.supplier?.name}</Typography></TableCell>
                            <TableCell sx={td(last)}><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{s.supplier?.gst_number || '—'}</Typography></TableCell>
                            <TableCell sx={td(last)}>
                              <Tooltip title={s.is_primary === 'Y' ? 'Primary — click to unmark' : 'Set as primary'}>
                                <IconButton size="small" onClick={() => handleTogglePrimary(s)}>
                                  {s.is_primary === 'Y' ? <StarIcon fontSize="small" sx={{ color: '#F9A825' }} /> : <StarBorderIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={td(last)}>
                              <Tooltip title="Unlink Supplier">
                                <IconButton size="small" onClick={() => handleUnlinkSupplier(s)}><DeleteIcon fontSize="small" color="error" /></IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </MDBox>
            </Card>

            {/* Price History */}
            <Card sx={{ mb: 4 }}>
              {gradientHeader('Price History', outlineWhiteBtn('Set New Price', <AddIcon />, () => { setEditingPrice(null); setPriceDialog(true); }))}
              <MDBox px={2} pt={2} pb={3}>
                {priceHistory.length === 0 ? (
                  <Typography sx={{ textAlign: 'center', py: 3, color: 'text.secondary', fontSize: '0.875rem' }}>No price history yet. Use &ldquo;Set New Price&rdquo; to log a price.</Typography>
                ) : (
                  <Table sx={{ boxShadow: 'none' }}>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={TH}>Variant</TableCell>
                        <TableCell sx={TH}>MRP (₹)</TableCell>
                        <TableCell sx={TH}>Purchase (₹)</TableCell>
                        <TableCell sx={TH}>Discount %</TableCell>
                        <TableCell sx={TH}>Effective From</TableCell>
                        <TableCell sx={TH}>Effective To</TableCell>
                        <TableCell sx={TH}>Remarks</TableCell>
                        <TableCell sx={TH}>Actions</TableCell>
                      </TableRow>
                      {priceHistory.map((p, idx, arr) => {
                        const last = idx === arr.length - 1;
                        const isCurrent = p.effective_to === null || p.effective_to === undefined;
                        return (
                          <TableRow key={p.id} hover sx={{ bgcolor: isCurrent ? TEAL + '07' : 'inherit', '&:hover': { bgcolor: isCurrent ? TEAL + '12' : 'rgba(0,0,0,0.02)' } }}>
                            <TableCell sx={td(last)}><Typography variant="caption" fontWeight={500}>{p.variant?.variant_name || '(Base Item)'}</Typography></TableCell>
                            <TableCell sx={td(last)}><Typography variant="caption" fontWeight={700} sx={{ color: TEAL }}>₹{parseFloat(p.mrp).toFixed(2)}</Typography></TableCell>
                            <TableCell sx={td(last)}><Typography variant="caption">{p.purchase_price !== null && p.purchase_price !== undefined ? `₹${parseFloat(p.purchase_price).toFixed(2)}` : '—'}</Typography></TableCell>
                            <TableCell sx={td(last)}><Typography variant="caption">{p.discount_percentage}%</Typography></TableCell>
                            <TableCell sx={td(last)}><Typography variant="caption">{p.effective_from}</Typography></TableCell>
                            <TableCell sx={td(last)}>
                              {isCurrent ? <Chip label="Current" size="small" sx={{ bgcolor: TEAL + '18', color: TEAL, fontWeight: 700, fontSize: '0.7rem' }} /> : <Typography variant="caption">{p.effective_to}</Typography>}
                            </TableCell>
                            <TableCell sx={td(last)}><Typography variant="caption" color="text.secondary">{p.remarks || '—'}</Typography></TableCell>
                            <TableCell sx={td(last)}>
                              <Box display="flex" gap={0.5}>
                                <Tooltip title="Edit price">
                                  <IconButton size="small" onClick={() => { setEditingPrice(p); setPriceDialog(true); }}>
                                    <EditIcon fontSize="small" sx={{ color: TEAL }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete price record">
                                  <IconButton size="small" onClick={() => handleDeletePrice(p)}>
                                    <DeleteIcon fontSize="small" color="error" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </MDBox>
            </Card>
          </>
        )}
        {/* Bottom save bar */}
        <MDBox display="flex" justifyContent="center" mt={2} mb={1}>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ ...btnTeal, px: 7, py: 1.3, fontSize: '0.95rem', borderRadius: 2, boxShadow: `0 4px 16px rgba(0,121,142,0.35)`, '&:hover': { background: '#016476', boxShadow: `0 6px 20px rgba(0,121,142,0.45)`, color: 'white !important' } }}>
            {saving ? 'Saving…' : (isNew ? '✓  Create Item' : '✓  Save Changes')}
          </Button>
        </MDBox>
      </MDBox>
      <Footer />

      <VariantDialog
        open={variantDialog}
        onClose={() => setVariantDialog(false)}
        onSaved={loadItem}
        itemId={itemId}
        initial={editingVariant}
        item={item}
        units={units}
        hsnData={selectedHsn}
      />
      <PriceDialog
        open={priceDialog}
        onClose={() => { setPriceDialog(false); setEditingPrice(null); }}
        onSaved={loadPriceHistory}
        itemId={itemId}
        item={item}
        variants={item?.variants || []}
        units={units}
        hsnData={selectedHsn}
        initial={editingPrice}
      />
      <MiniMasterDialog
        open={hsnMiniOpen}
        title={HSN_MINI_CONFIG.title}
        initialName={hsnMiniName}
        fields={HSN_MINI_CONFIG.fields}
        build={HSN_MINI_CONFIG.build}
        create={HSN_MINI_CONFIG.create}
        onClose={() => setHsnMiniOpen(false)}
        onCreated={handleHsnCreated}
      />
    </DashboardLayout>
  );
}
